// src/components/common/Footer.jsx
import React from 'react';
import {
    Box, Container, Grid, Typography, Link,
    IconButton, Divider, Button, Chip,
} from '@mui/material';
import {
    GitHub as GitHubIcon,
    Email as EmailIcon,
    Article as ArticleIcon,
    CalendarMonth as CalendarIcon,
    Code as CodeIcon,
    ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import theme, { designTokens } from '../../styles/theme';

const footerSections = [
    {
        title: 'Product',
        links: [
            { label: 'Analysis',      href: '/analysis' },
            { label: 'Research',      href: '/research' },
            { label: 'Comparison',    href: '/comparison' },
            { label: 'Documentation', href: '/documentation' },
            { label: 'Book a Demo',   href: '/book-demo' },
        ],
    },
    {
        title: 'Frameworks',
        links: [
            { label: 'HIPE',        href: '/documentation#hipe' },
            { label: 'RULA',        href: '/documentation#rula' },
            { label: 'REBA',        href: '/documentation#reba' },
            { label: 'RAMP',        href: '/documentation#ramp' },
            { label: 'Wergonomics', href: '/documentation#wergonomics' },
        ],
    },
    {
        title: 'Community',
        links: [
            { label: 'GitHub',       href: 'https://github.com/Admiral-White/Autoposturecv', external: true },
            { label: 'Contributing', href: '/documentation#contributing' },
            { label: 'Citation',     href: '/research#citation' },
            { label: 'Sponsorship',  href: '/sponsorship' },
        ],
    },
    {
        title: 'Contact',
        links: [
            { label: 'Email Support',   href: 'mailto:mikeinyang975@gmail.com', external: true },
            { label: 'Technical Consultation', href: '/technical-consultation' },
            { label: 'Report Issue',    href: 'https://github.com/Admiral-White/Autoposturecv/issues', external: true },
            { label: 'Feature Request', href: 'https://github.com/Admiral-White/Autoposturecv/issues', external: true },
        ],
    },
];

const Footer = () => {
    const navigate = useNavigate();

    return (
        <Box
            component="footer"
            sx={{
                bgcolor: '#0f172a',
                color: 'white',
                mt: 'auto',
                borderTop: '3px solid transparent',
                backgroundImage: `linear-gradient(#0f172a, #0f172a), ${theme.palette.gradients.primary}`,
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                paddingBottom: { xs: '80px', md: 0 }, // Extra padding on mobile to clear fixed bottom nav
            }}
        >
            <Container maxWidth="lg" sx={{ pt: 7, pb: 4 }}>
                <Grid container spacing={4}>

                    {/* Brand column */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <Box
                                sx={{
                                    width: 44, height: 44, borderRadius: 2,
                                    background: theme.palette.gradients.primary,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: designTokens.shadows.glow.primary,
                                    flexShrink: 0,
                                }}
                            >
                                <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '1.1rem' }}>AP</Typography>
                            </Box>
                            <Box>
                                <Typography 
                                    variant="h6" 
                                    fontWeight={800} 
                                    sx={{ 
                                        lineHeight: 1.2,
                                        fontSize: { xs: '0.95rem', sm: '1.25rem' },
                                    }}
                                >
                                    AutoPostureCV
                                </Typography>
                                <Chip
                                    label="v2.0.0 · Production"
                                    size="small"
                                    sx={{ height: 18, fontSize: '0.65rem', bgcolor: designTokens.overlays.lighter, color: 'rgba(255,255,255,0.6)', mt: 0.5 }}
                                />
                            </Box>
                        </Box>

                        <Typography 
                            variant="body2" 
                            sx={{ 
                                mb: 3, 
                                opacity: 0.6, 
                                lineHeight: 1.7, 
                                maxWidth: 320,
                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                            }}
                        >
                            Automated posture analysis with computer vision. Supporting 5 ergonomic
                            assessment frameworks for research, industry, and workplace safety.
                        </Typography>

                        {/* Social icons */}
                        <Box sx={{ display: 'flex', gap: 0.5, mb: 3 }}>
                            {[
                                { icon: <GitHubIcon fontSize="small" />, href: 'https://github.com/Admiral-White/Autoposturecv', label: 'GitHub' },
                                { icon: <EmailIcon fontSize="small" />,  href: 'mailto:mikeinyang975@gmail.com', label: 'Email' },
                                { icon: <ArticleIcon fontSize="small" />, href: '#', label: 'Docs' },
                            ].map((s) => (
                                <IconButton
                                    key={s.label}
                                    component="a"
                                    href={s.href}
                                    target={s.href.startsWith('http') ? '_blank' : undefined}
                                    aria-label={s.label}
                                    size="small"
                                    sx={{
                                        color: 'rgba(255,255,255,0.5)',
                                        border: `1px solid ${designTokens.overlays.lighter}`,
                                        borderRadius: 2,
                                        '&:hover': { color: 'white', bgcolor: designTokens.overlays.lighter, borderColor: designTokens.overlays.light },
                                    }}
                                >
                                    {s.icon}
                                </IconButton>
                            ))}
                        </Box>

                        {/* Book Demo CTA */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CalendarIcon sx={{ fontSize: '1rem !important' }} />}
                                endIcon={<ArrowIcon sx={{ fontSize: '0.9rem !important' }} />}
                                onClick={() => navigate('/book-demo')}
                                sx={{
                                    color: 'rgba(255,255,255,0.8)',
                                    borderColor: designTokens.overlays.lighter,
                                    borderRadius: 2,
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    '&:hover': {
                                        borderColor: 'rgba(255,255,255,0.5)',
                                        bgcolor: designTokens.overlays.lighter,
                                        color: 'white',
                                        transform: 'none',
                                    },
                                }}
                            >
                                Book a Demo
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CodeIcon sx={{ fontSize: '1rem !important' }} />}
                                endIcon={<ArrowIcon sx={{ fontSize: '0.9rem !important' }} />}
                                onClick={() => navigate('/technical-consultation')}
                                sx={{
                                    color: 'rgba(255,255,255,0.8)',
                                    borderColor: designTokens.overlays.lighter,
                                    borderRadius: 2,
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    '&:hover': {
                                        borderColor: 'rgba(255,255,255,0.5)',
                                        bgcolor: designTokens.overlays.lighter,
                                        color: 'white',
                                        transform: 'none',
                                    },
                                }}
                            >
                                Technical Consultation
                            </Button>
                        </Box>
                    </Grid>

                    {/* Link columns */}
                    {footerSections.map((section) => (
                        <Grid item xs={6} sm={3} md={2} key={section.title}>
                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block', mb: 2,
                                    opacity: 0.4, textTransform: 'uppercase',
                                    letterSpacing: '0.1em', fontWeight: 700,
                                    fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                }}
                            >
                                {section.title}
                            </Typography>
                            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {section.links.map((link) => (
                                    <li key={link.label}>
                                        <Link
                                            href={link.href}
                                            target={link.external ? '_blank' : undefined}
                                            rel={link.external ? 'noopener noreferrer' : undefined}
                                            underline="none"
                                            sx={{
                                                color: 'rgba(255,255,255,0.55)',
                                                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                                transition: 'color 0.15s ease',
                                                '&:hover': { color: 'white' },
                                            }}
                                        >
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ my: 4, borderColor: designTokens.overlays.lighter }} />

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 2, sm: 1 },
                    }}
                >
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            opacity: 0.4,
                            wordBreak: 'break-word',
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            lineHeight: { xs: 1.4, sm: 1.2 },
                            maxWidth: { xs: '100%', sm: 'auto' },
                        }}
                    >
                        © 2026 AutoPostureCV · MIT License · Developed by Mkpouto Shedrack & <Link
                            href="https://mikeinyang.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="none"
                            sx={{
                                color: 'inherit',
                                textDecoration: 'underline',
                                opacity: 0.6,
                                transition: 'opacity 0.15s ease',
                                '&:hover': { opacity: 1 },
                            }}
                        >
                            Michael Inyang
                        </Link>
                    </Typography>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            opacity: 0.4,
                            wordBreak: 'break-word',
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            lineHeight: { xs: 1.4, sm: 1.2 },
                            maxWidth: { xs: '100%', sm: 'auto' },
                        }}
                    >
                        Built with React · MUI · Computer Vision
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default Footer;
